import { useCallback, useEffect, useRef } from "react"
import { getAutosave } from "./prefsSlice"
import { useSelector } from "./store"

const AUTOSAVE_DELAY = Math.E * 1000

export default (callback: () => Promise<unknown>, isDirty: boolean) => {
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  const autosave = useSelector(getAutosave)
  const isDirtyRef = useRef(isDirty)
  const autosaveRef = useRef(autosave)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isSavingRef = useRef(false)

  useEffect(() => {
    isDirtyRef.current = isDirty
  }, [isDirty])
  useEffect(() => {
    autosaveRef.current = autosave
  }, [autosave])

  // Callers invoke this on each edit, so the timer restarts (a true debounce).
  const restart = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    if (!autosaveRef.current || !isDirtyRef.current || isSavingRef.current) {
      return
    }

    timerRef.current = setTimeout(() => {
      isSavingRef.current = true
      void callbackRef.current().then(
        () => {
          isSavingRef.current = false
          restart() // Picks up edits made during the save
        },
        () => {
          isSavingRef.current = false
        },
      )
    }, AUTOSAVE_DELAY)
  }, [])

  useEffect(() => {
    if (isDirty && autosave) {
      restart()
    } else if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [isDirty, autosave, restart])

  // Callers cancel before a manual save, or the armed timer fires a duplicate.
  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  return { isEnabled: autosave, restart, cancel }
}
